import * as React from 'react';
import { IntlContext, defineMessages } from 'react-intl';
import { Links, Meta, Scripts, ScrollRestoration } from 'react-router';

import { LocaleContext } from '@dnc/shared-components/LocaleContext';

/**
 * These messages are used in the {@link IndexHtml} component. We’re not
 * using the React components for this because these go into attributes or
 * elements that require string content only.
 */
const MESSAGES = defineMessages({
  title: {
    defaultMessage: 'I Will Vote',
    id: 'wrapper.appName',
  },
  description: {
    defaultMessage:
      'Be a voter. Find everything you need to vote in your state’s next election.',
    id: 'Ym8AwK',
    description: 'Generic meta description for the site',
  },
});

/**
 * Our overall HTML structure for IWV. This will be pre-rendered into index.html
 * and index.es.html files that can be served by CloudFront for IWillVote.com
 * and VoyAVotar.com, respectively.
 *
 * See the {@link Layout} component in root.tsx.
 */
export default function IndexHtml({
  children,
}: {
  children: React.ReactNode;
}): React.ReactNode {
  const locale = React.useContext(LocaleContext);
  const intl = React.useContext(IntlContext);

  let url: string;
  let socialImageUrl: string;

  if (locale === 'es') {
    url = 'https://voyavotar.com/';
    socialImageUrl = 'https://voyavotar.com/voyavotar-social.png';
  } else {
    url = 'https://iwillvote.com/';
    socialImageUrl = 'https://iwillvote.com/iwillvote-social.png';
  }

  return (
    <html lang={locale}>
      <head>
        <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
        <meta charSet="utf-8" />
        <meta httpEquiv="Content-Language" content={locale} />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, shrink-to-fit=no"
        />

        <link
          rel="icon"
          href="/i-will-vote-favicon-16.png"
          sizes="16x16"
          type="image/x-icon"
        />
        <link
          rel="icon"
          href="/i-will-vote-favicon-32.png"
          sizes="32x32"
          type="image/x-icon"
        />
        <link rel="icon" type="image/x-icon" href="/favicon.ico" />
        <link rel="stylesheet" href="https://use.typekit.net/cbx3pps.css" />
        <style
          type="text/css"
          dangerouslySetInnerHTML={{
            __html: '@import url("https://use.typekit.net/cbx3pps.css");',
          }}
        />

        <meta
          property="og:title"
          content={intl.formatMessage(MESSAGES.title)}
        />
        <meta
          property="og:description"
          content={intl.formatMessage(MESSAGES.description)}
        />
        <meta property="og:image" content={socialImageUrl} />
        <meta property="og:url" content={url} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content={url} />
        <meta name="twitter:creator" content="@TheDemocrats" />
        <meta
          name="twitter:title"
          content={intl.formatMessage(MESSAGES.title)}
        />
        <meta name="twitter:image" content={socialImageUrl} />
        <meta
          name="twitter:description"
          content={intl.formatMessage(MESSAGES.description)}
        />

        <title>{intl.formatMessage(MESSAGES.title)}</title>

        <meta
          name="description"
          content={intl.formatMessage(MESSAGES.description)}
        />

        <TranscendAirgapScript
          bundleId="b83d4a0e-0460-48a3-a324-6766df3b5753"
          useTestBundle={import.meta.env.MODE !== 'production'}
          // For now force report-only in production but let everything be
          // active in other environments for testing.
          reportOnly={import.meta.env.MODE === 'production'}
          logLevel={
            import.meta.env.MODE === 'production' ? 'error' : 'warn error info'
          }
        />
        <GtmScript gtmId="GTM-5HSJD3PQ" />

        <Meta />
        <Links />
      </head>

      <body>
        <main id="root">
          {children}

          <ScrollRestoration />
          <Scripts />
        </main>
      </body>
    </html>
  );
}

/**
 * Adds the Transcend airgap.js script as well as its UI script.
 *
 * Transcend implicitly connects with Google Tag Manager to update it with
 * consent information.
 *
 * To generate URLs for testing forcing regimes and DNT signals and such, visit
 * https://docs.transcend.io/docs/articles/consent-management/testing-and-troubleshooting/debugging-and-testing
 *
 * @see
 * https://docs.transcend.io/docs/articles/consent-management/reference/airgap/api
 */
const TranscendAirgapScript: React.FunctionComponent<{
  /**
   * Transcend bundle ID for our property.
   */
  bundleId: string;

  /**
   * If true, we will load the “test”-configured bundle instead of the
   * “production”-configured bundle. Should be generally set to true on staging
   * and likely dev.
   */
  useTestBundle?: boolean;

  /**
   * If true, enables collecting of requests and cookies that Airgap has allowed
   * through. Accessible via `airgap.export().sentRequests` and
   * `airgap.export().setCookies` in dev tools.
   */
  enableDebugMonitoring?: boolean;

  /**
   * Set this to "*" to see all logging. Defaults to "warn error".
   */
  logLevel?: string;

  /**
   * If true, then we only send cookie and request info to Transcend. In
   * reportOnly mode you can’t trigger the consent dialog or anything like that.
   *
   * If left unset, falls back to the Transcend default.
   */
  reportOnly?: boolean | undefined | null;
}> = ({
  bundleId,
  useTestBundle,
  enableDebugMonitoring,
  logLevel = 'warn error',
  reportOnly,
}) => {
  const deployPath = useTestBundle ? 'cm-test' : 'cm';

  return (
    <>
      {/* We load this script without async so that it’s running before any page
          requests start happening so it can control them. */}
      <script
        src={`https://transcend-cdn.com/${deployPath}/${bundleId}/airgap.js`}
        // Disable having airgap.js load the UI script because it will add it to
        // the DOM at a time that will conflict with React page hydration.
        // Instead we load it directly below.
        data-ui="off"
        data-log={logLevel}
        data-report-only={
          reportOnly === true ? 'on' : reportOnly === false ? 'off' : undefined
        }
        data-monitoring={enableDebugMonitoring ? 'export' : undefined}
      />

      {/* Transcend’s UI script for showing the cookie banner. */}
      <script
        async
        src={`https://transcend-cdn.com/${deployPath}/${bundleId}/ui.js`}
      />
    </>
  );
};

/**
 * Adds bootstrap code and async script tag to load Google Tag Manager code onto
 * the page.
 *
 * Our GTM container and workspace are configured at:
 * https://tagmanager.google.com/#/container/accounts/6248434634/containers/194749123/workspaces/23
 *
 * We expect that the GTM container will include the Google Analytics tag, so we
 * don’t need to handle any Google Analytics–specific stuff in this code.
 */
const GtmScript: React.FunctionComponent<{
  gtmId: string;
}> = ({ gtmId }) => {
  return (
    <>
      <script
        type="text/javascript"
        dangerouslySetInnerHTML={{
          // Adapted from the GTM sample code to initialize the data layer, but
          // minus the code to add a `<script>` tag since we do that manually
          // below to play nice with React hydration.
          //
          // This _must_ use the `dataLayer` name for the attribute because
          // Transcend looks for that specifically and it does not appear to be
          // parameterizable.
          __html: `
(function (win, dataLayerAttrName) {
  win[dataLayerAttrName] = win[dataLayerAttrName] || [];
  win[dataLayerAttrName].push({
    'gtm.start': new Date().getTime(),
    event: 'gtm.js',
  });
})(window, 'dataLayer');`,
        }}
      />

      {/* We add the script tag explicitly with React because when it’s added with
        `createElement` like in the sample code, React detects the <script> tag as a
        change when it does initial hydration and logs a warning. */}
      <script
        src={`https://www.googletagmanager.com/gtm.js?id=${gtmId}`}
        async
      ></script>
    </>
  );
};
